import { Col, Row, Card, Alert, Form, Button } from 'react-bootstrap';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import Pagination from './../../../../components/shared/Pagination';
import { LoadOrders, LoadSearchedOrders } from '../../../../features/Order/OrderSlice';
import { loaderActions } from '../../../../features/Loader/LoaderSlice';
import DownloadMultipleOrderLines from '../../../../components/shared/Files/DownloadMultipleOrderLines';
import UploadMultipleOrdersLines from '../../../../components/shared/Files/UploadMultipleOrderLines';
import { formatDate } from '../../../../Helpers/date';
import AdvanceHeadersSearch from '../../../../components/shared/AdvanceSearch/HeadersSearch';
import { VendorToCompanyID } from '../../../../components/shared/Company';
import Transfer from '../../../../components/shared/Order/Transfer';
import { usePreviousValue } from '../../../../hooks/usePreviousValue';
import { isVisibleToVendor } from '../../../../Helpers/transferOrderFilters';

function TransferOrders() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentVendor } = useSelector((state) => state.vendor);
  const previousVendorValue = usePreviousValue(currentVendor.vendNo);
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.loader.open);
  const orders = useSelector((state) => state.order.list);
  const { count, fetchingOrder } = useSelector((state) => state.order);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterOrders, setFilterOrders] = useState([]);
  const [isFirstLoad, setisFirstLoad] = useState(true);
  const [dateRange, setDateRange] = useState(() => {
    if (searchParams.get('range') && searchParams.get('range') === 'none') {
      return null;
    }
    if (searchParams.get('startDate') && searchParams.get('endDate')) {
      return [new Date(searchParams.get('startDate')), new Date(searchParams.get('endDate'))];
    }
    return [new Date(new Date().setDate(new Date().getDate() - 180)), new Date()];
  });

  const [skipPaginate, setSkipPaginate] = useState(() => {
    if (searchParams.get('skipPaginate') && searchParams.get('skipPaginate') === 'true') {
      return true;
    }
    return false;
  });
  const minDate = new Date(new Date().setDate(new Date().getDate() - 45));
  const maxDate = new Date(new Date().setDate(new Date().getDate() + 7));

  useEffect(() => {
    const queryParams = {};
    for (const p of searchParams.keys()) {
      queryParams[p] = searchParams.get(p);
    }
    if (searchParams.get('pageSize')) {
      setPageSize(Number(searchParams.get('pageSize')));
    } else {
      setSearchParams(`?${new URLSearchParams({ ...queryParams, pageSize: 10 })}`);
    }
    if (searchParams.get('currentPage')) {
      setCurrentPage(Number(searchParams.get('currentPage')));
    } else {
      setSearchParams(`?${new URLSearchParams({ ...queryParams, currentPage })}`);
    }
  }, []);

  async function updateParams() {
    const queryParams = {};
    for (const p of searchParams.keys()) {
      queryParams[p] = searchParams.get(p);
    }
    if (dateRange !== null) {
      delete queryParams.range;
      queryParams.startDate = await formatDate(dateRange[0]);
      queryParams.endDate = await formatDate(dateRange[1]);
    } else {
      delete queryParams.startDate;
      delete queryParams.endDate;
      queryParams.range = 'none';
    }
    queryParams.skipPaginate = skipPaginate;
    let page;
    if (isFirstLoad) {
      if (Number(searchParams.get('currentPage')) > currentPage) {
        page = Number(searchParams.get('currentPage'));
      } else {
        page = currentPage;
      }
    } else {
      page = currentPage;
    }
    setSearchParams(`?${new URLSearchParams({ ...queryParams, pageSize, currentPage: page })}`);
  }

  async function fetchOrdersList() {
    let dateFilter = '';
    if (dateRange !== null) {
      const startDate = await formatDate(dateRange[0]);
      const endDate = await formatDate(dateRange[1]);
      dateFilter = `and expectedReceiptDate gt ${startDate} and expectedReceiptDate lt ${endDate}`;
    }
    let page = currentPage;
    if (isFirstLoad) {
      if (Number(searchParams.get('currentPage')) > currentPage) {
        page = Number(searchParams.get('currentPage'));
      }
    }
    if (Object.keys(currentVendor).length > 0 && !fetchingOrder) {
      await dispatch(loaderActions.show());
      await dispatch(
        LoadOrders({
          endpoint: '/transfer_order_header',
          companyId: VendorToCompanyID(currentVendor),
          filter: `whseVendorCode eq '${currentVendor.vendNo}' ${dateFilter}`,
          type: 'list',
          currentPage: page,
          pageSize,
          skipPaginate
        })
      );
      await dispatch(loaderActions.hide());
    }
  }

  useEffect(() => {
    updateParams();
    fetchOrdersList();
    if (previousVendorValue !== undefined && Object.keys(currentVendor).length > 0) {
      if (previousVendorValue !== currentVendor.vendNo) {
        setCurrentPage(1);
      }
    }
  }, [currentVendor, pageSize, currentPage, dateRange, skipPaginate]);

  const orderList = filterOrders.map((order, index) => (
    <Link
      to={`/transfer-order/${currentVendor.VendorType}/${order.docNo}${location.search}`}
      key={index}
    >
      <Card className="shadow-sm p-3">
        <Transfer vendorType={currentVendor.VendorType} order={order} />
      </Card>
    </Link>
  ));

  const DateErrorCard = (
    <>
      <Alert variant="warning">
        {currentVendor !== undefined ? (
          `No Transfer orders for ${currentVendor.vendName} with current date filter`
        ) : (
          <> No Transfer orders with current date filter</>
        )}
      </Alert>
    </>
  );

  const ErrorCard = (
    <>
      <Alert variant="warning">
        {currentVendor !== undefined ? (
          `No Transfer orders for ${currentVendor.vendName}`
        ) : (
          <> No Transfer orders</>
        )}
      </Alert>
    </>
  );

  const SearchErrorCard = (
    <>
      <Alert variant="warning">
        <>No Transfer order matching current search</>
      </Alert>
    </>
  );

  const HiddenOrdersCard = (
    <>
      <Alert variant="warning">
        <>No Transfer orders currently available to view</>
      </Alert>
    </>
  );

  async function loadFilteredOrders() {
    await dispatch(loaderActions.show());
    await dispatch(
      LoadSearchedOrders({
        endpoint: '/transfer_order_header',
        companyId: VendorToCompanyID(currentVendor),
        filter: `whseVendorCode eq '${currentVendor.vendNo}' and contains(docNo,'${search}')`
      })
    );
    await dispatch(loaderActions.hide());
  }

  useEffect(() => {
    const filterOrders = orders
      .filter((order) => order.docNo.toLowerCase().includes(search.trim().toLowerCase()))
      .filter(isVisibleToVendor);
    setFilterOrders(filterOrders);
  }, [orders]);

  useEffect(() => {
    const filterOrders = orders
      .filter((order) => order.docNo.toLowerCase().includes(search.trim().toLowerCase()))
      .filter(isVisibleToVendor);
    setFilterOrders(filterOrders);

    if (filterOrders.length === 0 && search.trim() !== '') {
      loadFilteredOrders();
    }
  }, [search]);

  return (
    <>
      <Row>
        <Col md={{ span: 3 }} sm={{ span: 6 }} xs={{ span: 12 }}>
          <Button variant="link" onClick={() => setSkipPaginate(!skipPaginate)}>
            {skipPaginate === true ? 'Apply Pagination' : 'Show All'}
          </Button>
        </Col>
        <Col md={{ span: 3 }} sm={{ span: 6 }} xs={{ span: 12 }}>
          <DateRangePicker
            minDate={minDate}
            maxDate={maxDate}
            onChange={(e) => {
              setDateRange(e);
            }}
            value={dateRange}
          />
        </Col>

        <Col md={{ span: 6 }} sm={{ span: 6 }} xs={{ span: 12 }}>
          <div className="d-flex justify-content-end">
            <Form.Control
              size="sm"
              type="text"
              className="mr-2"
              placeholder="Search TO by #"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div>
              <AdvanceHeadersSearch
                query={search}
                type="transfer"
                vendorNo={currentVendor.vendNo}
                vendorType={currentVendor.VendorType}
                directLink="/transfer-order"
              />
            </div>
            <UploadMultipleOrdersLines
              vendorNo={currentVendor.vendNo}
              vendName={currentVendor.vendName}
              vendorType={currentVendor.VendorType}
              vpFreightType={currentVendor.vpFreightType}
              directLink="/transfer-order"
              type="transfer"
            />
            {orders.length > 0 && (
              <DownloadMultipleOrderLines
                endpoint={'/transfer_order_lines'}
                vendorNo={currentVendor.vendNo}
                vendName={currentVendor.vendName}
                vendorType={currentVendor.VendorType}
                vpFreightType={currentVendor.vpFreightType}
                dateRange={dateRange !== null ? dateRange : []}
                type="transfer"
              />
            )}
          </div>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col xl={12} md={12}>
          {filterOrders.length > 0 ? (
            orderList
          ) : orders.length > 0 && search.trim() !== '' ? (
            SearchErrorCard
          ) : orders.length > 0 ? (
            HiddenOrdersCard
          ) : loading ? (
            <></>
          ) : dateRange === null ? (
            ErrorCard
          ) : (
            DateErrorCard
          )}
          {skipPaginate === false &&
            count > pageSize &&
            (search.trim().length === 0 ? (
              <Card className="shadow-sm pt-2">
                <Card.Body>
                  <Row>
                    <Col>
                      <p>Total Orders: {count}</p>
                      <p>
                        Currently Viewing : {(currentPage - 1) * pageSize + 1} to{' '}
                        {currentPage * pageSize > count ? count : currentPage * pageSize}
                      </p>
                    </Col>
                    <Col className="text-center">
                      <Form.Group className="mt-2">
                        <Form.Control
                          as="select"
                          onChange={(e) => {
                            setCurrentPage(1);
                            setPageSize(e.target.value);
                          }}
                        >
                          <option value="10">Orders per page: 10</option>
                          <option value="20">Orders per page: 20</option>
                          <option value="25">Orders per page: 25</option>
                          <option value="50">Orders per page: 50</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col className="text-right mt-2">
                      <Pagination
                        className="pagination-bar"
                        currentPage={currentPage}
                        totalCount={count}
                        pageSize={pageSize}
                        onPageChange={(page) => {
                          setisFirstLoad(false);
                          setCurrentPage(page);
                        }}
                      />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ) : (
              <></>
            ))}
        </Col>
      </Row>
    </>
  );
}

export default TransferOrders;
